import { ApiSuccessSchema } from "../../../common/response.schema";
import {
  AttachVibeGitProjectBodySchema,
  AttachVibeGitProjectPathVarsSchema,
  AttachVibeGitProjectResponseSchema,
  CreateVibeGitProjectBodySchema,
  CreateVibeGitProjectResponseSchema,
  GetVibeGitProjectByIdPathVarsSchema,
  GetVibeGitProjectByIdResponseSchema,
  GetVibeGitProjectPathVarsSchema,
  GetVibeGitProjectResponseSchema,
  ListVibeGitProjectsResponseSchema,
  ProvisionVibeGitProjectBodySchema,
  ProvisionVibeGitProjectPathVarsSchema,
  ProvisionVibeGitProjectResponseSchema,
  ReprovisionVibeGitProjectPathVarsSchema,
  ReprovisionVibeGitProjectResponseSchema
} from "../schemas";

export const VibeGitProjectEndpoints = {
  // ---------------------------------------------------------------------------
  // VibeGitProject — the git primitive; apps point at a project
  // (VibeApp.gitProjectId, many apps → one project). These app-scoped
  // routes provision/read the project ATTACHED to an app; the backend
  // also serves each under the legacy `/repository` path alias for
  // pre-decoupling CLIs.
  // ---------------------------------------------------------------------------

  /**
   * Create a git project for an app and attach it. Lands in PENDING; the
   * tenant agent advances it to READY once the Forgejo repo materializes.
   * The project takes the app's name + deploy branch as its identity.
   * 409 if the app already has a project or the name is taken in the org.
   */
  // NOTE: these three PATHS deliberately stay on the legacy `repository`
  // segment for one release: clients built from this contract (the npm CLI
  // publishes on the staging merge) reach production backends that predate
  // the rename for days. The backend already mounts the canonical
  // `git-project` segment as a sibling route — flip these paths once the
  // fleet is past the pre-decoupling backend.
  ProvisionGitProject: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/repository",
    PathVars: ProvisionVibeGitProjectPathVarsSchema,
    Body: ProvisionVibeGitProjectBodySchema,
    Response: ApiSuccessSchema(ProvisionVibeGitProjectResponseSchema)
  },

  /**
   * Fetch the git project attached to an app. 404 if the app has none (the
   * status field tells you where in the lifecycle it sits).
   */
  GetGitProject: {
    method: "GET" as const,
    path: "/api/vibe/apps/:appId/repository",
    PathVars: GetVibeGitProjectPathVarsSchema,
    Response: ApiSuccessSchema(GetVibeGitProjectResponseSchema)
  },

  /**
   * Re-arm a FAILED git project for another provisioning attempt. A project
   * whose in-cluster materialization failed is otherwise a dead end — the
   * create path 409s on the already-attached guard. This flips the FAILED
   * row back to PENDING so the agent re-materializes it on its next pull.
   * 404 if the app or its project is absent; 409 if the project is not
   * FAILED (READY / PENDING / ARCHIVED — nothing to retry).
   */
  ReprovisionGitProject: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/repository/reprovision",
    PathVars: ReprovisionVibeGitProjectPathVarsSchema,
    Response: ApiSuccessSchema(ReprovisionVibeGitProjectResponseSchema)
  },

  /**
   * Attach an EXISTING git project to an app. The counterpart to
   * `ProvisionGitProject`, which mints a NEW project named after the app and
   * is therefore useless once a project already holds the code.
   *
   * Without this edge an app created before its code store could never be
   * joined to it: pushes to the project advanced its refs and deployed
   * nothing, because the push fan-out only reaches apps LINKED to the
   * project — and that no-op is silent. See the schema for the full
   * semantics (idempotent on the same project, 409 on a different one).
   *
   * Unlike its three siblings above, this path uses the canonical
   * `git-project` segment with no legacy `repository` alias. The alias
   * carries pre-decoupling clients, and none of them can call a route that
   * did not exist before the decoupling.
   */
  AttachGitProject: {
    method: "POST" as const,
    path: "/api/vibe/apps/:appId/git-project/attach",
    PathVars: AttachVibeGitProjectPathVarsSchema,
    Body: AttachVibeGitProjectBodySchema,
    Response: ApiSuccessSchema(AttachVibeGitProjectResponseSchema)
  },

  // ---------------------------------------------------------------------------
  // VibeGitProject — the standalone surface: the primitive addressed on its
  // own id, no app in the picture. Unlike the app-scoped routes above these
  // paths need no legacy alias and no `repository` response key: they did not
  // exist before the decoupling, so no pre-decoupling client can call them.
  // (A client built from this contract that reaches a backend which predates
  // these routes gets a clean 404 — the endpoint genuinely isn't there yet.)
  // ---------------------------------------------------------------------------

  /**
   * Create a git project with no app attached — a pure code store. Lands in
   * PENDING; the tenant agent materializes the Forgejo repo and advances it
   * to READY. Its refs advance on push and nothing deploys until an app
   * attaches to it. 400 on an invalid name, 409 if the name is taken in the org.
   */
  CreateGitProject: {
    method: "POST" as const,
    path: "/api/vibe/git-projects",
    Body: CreateVibeGitProjectBodySchema,
    Response: ApiSuccessSchema(CreateVibeGitProjectResponseSchema)
  },

  /** List the org's git projects, newest first — standalone and attached alike. */
  ListGitProjects: {
    method: "GET" as const,
    path: "/api/vibe/git-projects",
    Response: ApiSuccessSchema(ListVibeGitProjectsResponseSchema)
  },

  /**
   * Fetch a git project by its own id. 404 if absent, soft-deleted, or owned
   * by another org (never 403 — a foreign id must not be distinguishable).
   */
  GetGitProjectById: {
    method: "GET" as const,
    path: "/api/vibe/git-projects/:id",
    PathVars: GetVibeGitProjectByIdPathVarsSchema,
    Response: ApiSuccessSchema(GetVibeGitProjectByIdResponseSchema)
  }
} as const;
